/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

 


// Validazione shopping bag al checkout - Sempre attiva
(function() {
  if (window.shoppingBagValidationInitialized) return;
  window.shoppingBagValidationInitialized = true;
  
  const SHOPPING_BAG_VARIANT_ID = '58726417727837';
  
  function hasOnlyShoppingBag(items) {
    if (!items || items.length === 0) return false;
    return items.every(item => item.variant_id.toString() === SHOPPING_BAG_VARIANT_ID);
  }
  
  function updateCheckoutButtons(disable) {
    //const buttons = document.querySelectorAll('#mu-checkout-button, [name="checkout"], .cart__checkout-button'); //Monster Upsell cart version
    const buttons = document.querySelectorAll('.cart-drawer__footer > .checkout-buttons > a.btn');
    
    buttons.forEach(btn => {
      if (disable) {
        btn.style.opacity = '0.5';
        btn.style.pointerEvents = 'none';
        btn.disabled = true;
        btn.title = 'Aggiungi almeno un prodotto oltre alla Shopping Bag';
      } else {
        btn.style.opacity = '1';
        btn.style.pointerEvents = 'auto';
        btn.disabled = false;
        btn.title = '';
      }
    });
  }
  
  function toggleWarning(show) {
    const existingWarning = document.getElementById('shopping-bag-warning');
    
    if (show && !existingWarning) {
      const warning = document.createElement('div');
      warning.id = 'shopping-bag-warning';
      warning.style.cssText = 'background: #fff3cd; border: 1px solid #ffc107; color: #856404; padding: 12px; margin: 10px 0; border-radius: 4px; font-size: 14px; text-align: center;';
      warning.textContent = '⚠️ Aggiungi almeno un prodotto al carrello oltre alla Shopping Bag per procedere al checkout.';
      
      //const checkoutButton = document.querySelector('#mu-checkout-button'); //Monster Upsell cart
      const checkoutButton = document.querySelector('.cart-drawer__footer > .checkout-buttons > a.btn');
      console.log(checkoutButton);
      if (checkoutButton?.parentNode) {
        checkoutButton.parentNode.insertBefore(warning, checkoutButton);
      }
    } else if (!show && existingWarning) {
      existingWarning.remove();
    }
  }
  
  window.mu_custom_checkout = function() {
    const cartItems = window.monsterCart?.items || [];
    
    if (hasOnlyShoppingBag(cartItems)) {
      alert('Non puoi acquistare solo la Shopping Bag. Aggiungi almeno un altro prodotto al carrello.');
      return false;
    }
    
    return true;
  };
  
  document.addEventListener('cart:updated', function({detail: { items }}) {
    const onlyShoppingBag = hasOnlyShoppingBag(items);
    updateCheckoutButtons(onlyShoppingBag);
    toggleWarning(onlyShoppingBag);
  });
})();




/* PB - LUCA BIONDI - ADDED KLAVIYO TRACK */
window.addEventListener("klaviyoForms", function(e) {
  if (e.detail.type === 'submit' && typeof fbq === 'function') {
    fbq('track', 'Lead');
  }
});
/* PB - LUCA BIONDI - ADDED KLAVIYO TRACK END HERE */



/* PB [MP] - adds a banner in Monster Drawer Cart with countdown and autohide

(function () {
  const EXPIRY = new Date('2026-05-04T23:59:59+02:00');
  if (new Date() > EXPIRY) return;

  const TARGET_ID = 'mu-items-container';
  const INJECTED_ID = 'my-custom-block';

  function getTimeLeft() {
    const diff = EXPIRY - new Date();
    if (diff <= 0) return null;
    return {
      h: Math.floor(diff / 1000 / 60 / 60),
      m: Math.floor((diff / 1000 / 60) % 60),
      s: Math.floor((diff / 1000) % 60),
    };
  }

  function pad(n) {
    return String(n).padStart(2, '0');
  }

  function startCountdown() {
    const el = document.getElementById(INJECTED_ID + '-timer');
    if (!el) return;

    const tick = setInterval(function () {
      const t = getTimeLeft();
      if (!t) {
        clearInterval(tick);
        const block = document.getElementById(INJECTED_ID);
        if (block) block.remove();
        return;
      }
      el.textContent = pad(t.h) + ':' + pad(t.m) + ':' + pad(t.s);
    }, 1000);
  }

  function injectBlock() {
    if (document.getElementById(INJECTED_ID)) return true;

    const target = document.getElementById(TARGET_ID);
    if (!target) return false;

    const t = getTimeLeft();
    if (!t) return true; // già scaduto

    const block = document.createElement('div');
    block.id = INJECTED_ID;
    block.innerHTML = `
      <div class="my-promo-banner">
        <!-- Il tuo contenuto promozionale qui -->
        <div class="promo" style="padding:5px 10px;text-align:center;border-bottom:1px solid #cbd5e0;font-size:0.75rem;"><strong>-20% se acquisti 2 o più capi</strong>
          <br><span class="my-promo-label">Offerta valida ancora per:</span>
          <span class="my-promo-timer" id="${INJECTED_ID}-timer">${pad(t.h)}:${pad(t.m)}:${pad(t.s)}</span>
        </div>
      </div>
    `;

    target.insertAdjacentElement('beforebegin', block);
    startCountdown();
    return true;
  }

  if (injectBlock()) return;

  const observer = new MutationObserver(function (mutations, obs) {
    if (injectBlock()) {
      obs.disconnect();
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  setTimeout(() => observer.disconnect(), 10000);
})(); */

/* PB - sostituzione testo "sporty" con logo nei sottotitoli overlay - commentato in attesa di adattamento per il cashmere
document.addEventListener('DOMContentLoaded', function () {
  const elements = document.querySelectorAll('.text-overlay__subheading');
  console.log('[sport-logo] elementi trovati:', elements.length, elements);

  elements.forEach(el => {
    console.log('[sport-logo] contenuto prima:', el.innerHTML);
    el.innerHTML = el.innerHTML.replace(
      /sporty/gi,
      '<img src="https://cdn.shopify.com/s/files/1/0578/1592/4809/files/logo_Sporty.png?v=1778059329" alt="Sporty" style="height:2.5em;width:auto;vertical-align:middle;display:inline-block;">'
    );
    console.log('[sport-logo] contenuto dopo:', el.innerHTML);
  });
});
*/